import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useConstants } from '@/contexts/ConstantsContext';
import { useAuth } from '@/contexts/AuthContext';
import { RefreshCw, GripVertical, Clock, AlertTriangle, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStageColor = (stage) => {
  const colors = {
    'Prospecting': 'bg-zinc-500',
    'Appointment Scheduled': 'bg-blue-500',
    'Attended': 'bg-emerald-500',
    'No show': 'bg-red-500',
    'Contract Sent': 'bg-orange-500',
    'Closed Won': 'bg-emerald-600',
    'Closed Lost': 'bg-red-600',
  };
  return colors[stage] || 'bg-zinc-500';
};

const LeadCard = ({ lead, onDragStart, canDrag }) => {
  const navigate = useNavigate();

  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => canDrag && onDragStart(e, lead)}
      onClick={() => navigate(`/leads/${lead.id}`)}
      className={`bg-secondary/50 border border-border rounded-lg p-3 hover:border-primary/30 transition-all group ${
        canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      }`}
      data-testid={`pipeline-card-${lead.id}`}
    >
      <div className="flex items-start gap-2">
        {canDrag && (
          <GripVertical className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{lead.full_name}</p>
          <p className="text-xs text-muted-foreground truncate">{lead.company_name}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="outline" className={`text-[10px] ${lead.country_market === 'UK' ? 'badge-uk' : 'badge-usa'}`}>
              {lead.country_market}
            </Badge>
            {lead.deal_value_estimate && (
              <span className="text-[10px] text-muted-foreground">
                £{lead.deal_value_estimate.toLocaleString()}
              </span>
            )}
            {/* Appointment status badge */}
            {lead.appointment_status && lead.appointment_status !== 'Scheduled' && (
              <Badge variant="outline" className={`text-[10px] ${
                lead.appointment_status === 'Attended' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                lead.appointment_status === 'No show' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                ''
              }`}>
                {lead.appointment_status === 'Attended' && <Check className="w-3 h-3 mr-1" />}
                {lead.appointment_status === 'No show' && <X className="w-3 h-3 mr-1" />}
                {lead.appointment_status}
              </Badge>
            )}
            {/* Local time with warning */}
            {lead.local_time && (
              <span className={`text-[10px] flex items-center gap-1 ${
                lead.outside_call_hours ? 'text-amber-400' : 'text-muted-foreground'
              }`}>
                <Clock className="w-3 h-3" />
                {lead.local_time}
                {lead.outside_call_hours && (
                  <AlertTriangle className="w-3 h-3" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Pipeline = () => {
  const navigate = useNavigate();
  const { isOwner, isAdmin, isSetter } = useAuth();
  const { admin_pipeline_view_stages, setter_allowed_stages } = useConstants();

  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState(null);

  // Setters see only their stages, Admins see the new pipeline stages (excluding Prospecting)
  const stagesToShow = isSetter 
    ? (setter_allowed_stages || ['Prospecting', 'Appointment Scheduled']) 
    : (admin_pipeline_view_stages || ['Appointment Scheduled', 'Attended', 'No show', 'Contract Sent', 'Closed Won', 'Closed Lost']);

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    try {
      const response = await axios.get(`${API}/leads/pipeline`);
      setPipeline(response.data);
    } catch (error) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!draggedLead || draggedLead.stage === targetStage) {
      setDraggedLead(null);
      return;
    }

    // Check setter permissions
    if (isSetter) {
      const allowedStages = setter_allowed_stages || ['Prospecting', 'Appointment Scheduled'];
      if (!allowedStages.includes(targetStage)) {
        toast.error(`Setters can only move leads to: ${allowedStages.join(', ')}`);
        setDraggedLead(null);
        return;
      }
    }

    try {
      await axios.put(`${API}/leads/${draggedLead.id}`, { stage: targetStage });
      
      // Show appropriate message based on stage
      if (targetStage === 'Attended') {
        toast.success(`Lead marked as Attended`);
      } else if (targetStage === 'No show') {
        toast.success(`Lead marked as No Show`);
      } else {
        toast.success(`Moved to ${targetStage}`);
      }
      
      fetchPipeline();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    } finally {
      setDraggedLead(null);
    }
  };

  // Check if a stage can accept drops
  const canDropToStage = (stage) => {
    if (isAdmin) return true;
    return (setter_allowed_stages || ['Prospecting', 'Appointment Scheduled']).includes(stage);
  };

  // Check if a lead can be dragged
  const canDragLead = (lead) => {
    if (isAdmin) return true;
    // Setters can only drag leads in allowed stages
    return (setter_allowed_stages || ['Prospecting', 'Appointment Scheduled']).includes(lead.stage);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pipeline</h1>
          <p className="text-muted-foreground mt-1">
            {isSetter ? 'Your leads - Drag to update stages' : 'Drag to Attended/No show to update appointment status'}
          </p>
          {isSetter && (
            <p className="text-xs text-amber-400 mt-1">
              You can only move leads between Prospecting and Appointment Scheduled
            </p>
          )}
        </div>
        <button onClick={fetchPipeline} className="p-2 hover:bg-secondary rounded-lg" data-testid="refresh-pipeline">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      <div className="kanban-container pb-4">
        {stagesToShow.map(stage => {
          const leads = pipeline[stage] || [];
          const totalValue = leads.reduce((sum, l) => sum + (l.deal_value_estimate || 0), 0);
          const canDrop = canDropToStage(stage);

          return (
            <div
              key={stage}
              className={`kanban-column ${!canDrop ? 'opacity-50' : ''}`}
              onDragOver={canDrop ? handleDragOver : undefined}
              onDrop={canDrop ? (e) => handleDrop(e, stage) : undefined}
              data-testid={`pipeline-column-${stage.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Card className="bg-card border-border h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getStageColor(stage)}`} />
                    <CardTitle className="text-sm font-semibold flex-1">{stage}</CardTitle>
                    <Badge variant="secondary" className="text-xs">{leads.length}</Badge>
                  </div>
                  {totalValue > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      £{totalValue.toLocaleString()}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="space-y-2 pr-2">
                      {leads.map(lead => (
                        <LeadCard
                          key={lead.id}
                          lead={lead}
                          onDragStart={handleDragStart}
                          canDrag={canDragLead(lead)}
                        />
                      ))}
                      {leads.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground text-xs">
                          No leads
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Pipeline;
